import React, { useState, useMemo, useEffect } from 'react';
import { 
  Card, 
  List, 
  Button, 
  Tooltip, 
  Avatar, 
  Badge,
  Space,
  Typography,
  Empty,
  Collapse,
  Tag
} from 'antd';
import { 
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  UserOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  SendOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getOrgSiteTypes } from '../../services/siteService';
import MassContactModal from './MassContactModal';
import AdvancedSMSComposer from '../messaging/AdvancedSMSComposer';

const { Text, Title } = Typography;
const { Panel } = Collapse;

/**
 * SiteSelectionPanel - Displays selected sites and provides mass contact functionality
 */
const SiteSelectionPanel = ({ 
  selectedSites = [], 
  className = '',
  showMassContact = true,
  title = 'Selected Sites',
  emptyMessage = 'No sites selected. Draw a shape on the map to select sites.'
}) => {
  const { currentUser } = useAuth();
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAdvancedSMS, setShowAdvancedSMS] = useState(false);
  const [groupBy, setGroupBy] = useState('none'); // none, type, region, status
  const [orgSiteTypes, setOrgSiteTypes] = useState([]);

  // Load organization site types
  useEffect(() => {
    const loadOrgSiteTypes = async () => {
      if (currentUser?.orgId) {
        try {
          const orgTypes = await getOrgSiteTypes(currentUser.orgId);
          setOrgSiteTypes(orgTypes);
        } catch (error) {
          console.error('Error loading org site types:', error);
        }
      }
    };
    
    loadOrgSiteTypes();
  }, [currentUser?.orgId]);

  // Site statistics
  const siteStats = useMemo(() => {
    const stats = {
      total: selectedSites.length,
      byType: {},
      byStatus: {},
      byRegion: {},
      withContacts: selectedSites.filter(site => 
        site.site_manager_mobile && site.site_manager_mobile.trim()
      ).length
    };

    selectedSites.forEach(site => {
      // By type
      stats.byType[site.site_type] = (stats.byType[site.site_type] || 0) + 1;
      
      // By status
      stats.byStatus[site.status || 'unknown'] = (stats.byStatus[site.status || 'unknown'] || 0) + 1;
      
      // By region
      const region = site.region || 'Unassigned';
      stats.byRegion[region] = (stats.byRegion[region] || 0) + 1;
    });

    return stats;
  }, [selectedSites]);

  // Group sites based on current grouping
  const groupedSites = useMemo(() => {
    if (groupBy === 'none') {
      return { 'All Sites': selectedSites };
    }

    const groups = {};
    selectedSites.forEach(site => {
      let groupKey;
      
      switch (groupBy) {
        case 'type':
          groupKey = site.site_type?.charAt(0).toUpperCase() + site.site_type?.slice(1) || 'Other';
          break;
        case 'region':
          groupKey = site.region || 'Unassigned';
          break;
        case 'status':
          groupKey = site.status?.charAt(0).toUpperCase() + site.status?.slice(1) || 'Unknown';
          break;
        default:
          groupKey = 'All Sites';
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(site);
    });

    return groups;
  }, [selectedSites, groupBy]);

  // Create dynamic site type colors from organization's registered types
  const SITE_TYPE_COLORS = useMemo(() => {
    const colors = {};
    
    // Convert org site types to colors object
    orgSiteTypes.forEach(type => {
      colors[type.name] = type.color;
    });
    
    // Fallback for unregistered types
    const fallbackColors = {
    other: '#95a5a6'
  };
    
    return { ...colors, ...fallbackColors };
  }, [orgSiteTypes]);

  // Status colors - Crisis Management focused
  const STATUS_COLORS = {
    active: '#27ae60',        // Green - operational
    degraded: '#f39c12',      // Orange - warning
    evacuating: '#e74c3c',    // Red - urgent action
    evacuated: '#9b59b6',     // Purple - completed evacuation
    closed_and_secure: '#3498db', // Blue - secure
    unknown: '#95a5a6'        // Gray - default/no info
  };

  // Get site avatar icon
  const getSiteAvatar = (site) => {
    const color = SITE_TYPE_COLORS[site.site_type] || SITE_TYPE_COLORS.other;
    return (
      <Avatar 
        style={{ 
          backgroundColor: color, 
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold'
        }}
        size="small"
      >
        {site.site_type?.charAt(0).toUpperCase() || 'S'}
      </Avatar>
    );
  };

  // Format address for display
  const formatAddress = (site) => {
    const parts = [
      site.address_line_1,
      site.suburb,
      site.state,
      site.postcode
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Get valid contacts for mass communication
  const getValidContacts = () => {
    return selectedSites.filter(site => 
      site.site_manager_mobile && 
      site.site_manager_mobile.trim() &&
      site.site_manager_name && 
      site.site_manager_name.trim()
    );
  };

  // Render site item
  const renderSiteItem = (site) => {
    const hasValidContact = site.site_manager_mobile && site.site_manager_mobile.trim();
    
    return (
      <List.Item
        key={site.id}
        className="site-list-item"
        style={{ 
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '8px',
          backgroundColor: '#fafafa',
          border: '1px solid #f0f0f0'
        }}
      >
        <List.Item.Meta
          avatar={getSiteAvatar(site)}
          title={
            <Space>
              <Text strong style={{ color: '#39325b' }}>
                {site.site_name}
              </Text>
              <Tag 
                color={SITE_TYPE_COLORS[site.site_type] || SITE_TYPE_COLORS.other}
                size="small"
              >
                {site.site_type?.charAt(0).toUpperCase() + site.site_type?.slice(1)}
              </Tag>
              {site.status && (
                <Tag 
                  color={STATUS_COLORS[site.status] || STATUS_COLORS.active}
                  size="small"
                >
                  {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
                </Tag>
              )}
            </Space>
          }
          description={
            <div>
              <div className="mb-1">
                <EnvironmentOutlined className="me-1 text-muted" />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {formatAddress(site)}
                </Text>
              </div>
              
              {site.region && (
                <div className="mb-1">
                  <InfoCircleOutlined className="me-1 text-muted" />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Region: {site.region}
                  </Text>
                </div>
              )}
              
              <div className="d-flex align-items-center gap-2 mt-2">
                <UserOutlined className="text-muted" style={{ fontSize: '12px' }} />
                <Text style={{ fontSize: '12px' }}>
                  {site.site_manager_name}
                </Text>
                
                {hasValidContact ? (
                  <Space size="small">
                    <Tooltip title={`Call ${site.site_manager_mobile}`}>
                      <Button
                        type="link"
                        size="small"
                        icon={<PhoneOutlined />}
                        onClick={() => window.open(`tel:${site.site_manager_mobile}`)}
                        style={{ padding: '0 4px', color: '#27ae60' }}
                      />
                    </Tooltip>
                    
                    {site.site_manager_email && (
                      <Tooltip title={`Email ${site.site_manager_email}`}>
                        <Button
                          type="link"
                          size="small"
                          icon={<MailOutlined />}
                          onClick={() => window.open(`mailto:${site.site_manager_email}`)}
                          style={{ padding: '0 4px', color: '#3498db' }}
                        />
                      </Tooltip>
                    )}
                  </Space>
                ) : (
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    No contact info
                  </Text>
                )}
              </div>
            </div>
          }
        />
      </List.Item>
    );
  };

  // Render statistics
  const renderStats = () => (
    <Card size="small" className="mb-3" style={{ borderRadius: '8px' }}>
      <div className="d-flex justify-content-between align-items-center">
        <Space>
          <Badge count={siteStats.total} showZero color="#39325b">
            <span>Total Sites</span>
          </Badge>
          
          <Badge count={siteStats.withContacts} showZero color="#27ae60">
            <span>With Contacts</span>
          </Badge>
        </Space>
        
        {showMassContact && siteStats.withContacts > 0 && (
          <Space>
            <Button
              icon={<MessageOutlined />}
              size="small"
              onClick={() => setShowContactModal(true)}
              style={{ backgroundColor: '#f0f0f0', borderColor: '#d9d9d9' }}
            >
              Basic SMS
            </Button>
            <Button
              type="primary"
              icon={<MessageOutlined />}
              size="small"
              onClick={() => setShowAdvancedSMS(true)}
              style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
            >
              Advanced SMS
            </Button>
          </Space>
        )}
      </div>
    </Card>
  );

  // Render grouping controls
  const renderGroupingControls = () => (
    <div className="mb-3">
      <Text type="secondary" style={{ fontSize: '12px', marginRight: '8px' }}>
        Group by:
      </Text>
      <Space>
        {[
          { key: 'none', label: 'None' },
          { key: 'type', label: 'Type' },
          { key: 'region', label: 'Region' },
          { key: 'status', label: 'Status' }
        ].map(option => (
          <Button
            key={option.key}
            size="small"
            type={groupBy === option.key ? 'primary' : 'default'}
            onClick={() => setGroupBy(option.key)}
            style={groupBy === option.key ? {
              backgroundColor: '#39325b',
              borderColor: '#39325b'
            } : {}}
          >
            {option.label}
          </Button>
        ))}
      </Space>
    </div>
  );

  if (selectedSites.length === 0) {
    return (
      <Card 
        className={className}
        title={title}
        style={{ borderRadius: '8px' }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={emptyMessage}
        />
      </Card>
    );
  }

  return (
    <div className={className}>
      <Card 
        title={title}
        style={{ borderRadius: '8px' }}
        styles={{ body: { padding: '16px' } }}
      >
        {renderStats()}
        
        {selectedSites.length > 1 && renderGroupingControls()}
        
        {groupBy === 'none' ? (
          <List
            dataSource={selectedSites}
            renderItem={renderSiteItem}
            className="site-selection-list"
            style={{ maxHeight: '400px', overflowY: 'auto' }}
          />
        ) : (
          <Collapse 
            ghost
            defaultActiveKey={Object.keys(groupedSites)}
            style={{ maxHeight: '400px', overflowY: 'auto' }}
          >
            {Object.entries(groupedSites).map(([groupName, sites]) => (
              <Panel 
                header={
                  <Space>
                    <Text strong>{groupName}</Text>
                    <Badge count={sites.length} showZero />
                  </Space>
                }
                key={groupName}
              >
                <List
                  dataSource={sites}
                  renderItem={renderSiteItem}
                  className="site-selection-list"
                />
              </Panel>
            ))}
          </Collapse>
        )}
      </Card>

      {/* Mass Contact Modal */}
      {showContactModal && (
        <MassContactModal
          visible={showContactModal}
          onClose={() => setShowContactModal(false)}
          selectedSites={getValidContacts()}
        />
      )}

      {/* Advanced SMS Composer */}
      {showAdvancedSMS && (
        <AdvancedSMSComposer
          visible={showAdvancedSMS}
          onClose={() => setShowAdvancedSMS(false)}
          selectedSites={getValidContacts()}
          onSendComplete={(results) => {
            console.log('Advanced SMS send results:', results);
            setShowAdvancedSMS(false);
          }}
        />
      )}
    </div>
  );
};

export default SiteSelectionPanel; 